import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Card, CardBody, Col } from "react-bootstrap";
import Select from "react-select";
import { IRoomName } from "../../shared/interfaces/room/IRoomName";
import { RoomNameService } from "./service";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { usePagination } from "../../shared/hooks/usePagination";
import DeleteModal from "../../Common/DeleteModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const PER_PAGE_DEFAULT = 20;

interface SortableRowProps {
  item: IRoomName;
  onEdit: (id: number) => void;
  onDelete: (item: IRoomName) => void;
  t: (key: string) => string;
}

const SortableRow = ({ item, onEdit, onDelete, t }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td {...listeners} style={{ cursor: "grab" }}>
        <i className="ti ti-grip-vertical f-18"></i>
      </td>
      <td>{item.id}</td>
      <td>{item.name}</td>
      <td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</td>
      <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "-"}</td>
      <td className="text-end">
        <ul className="list-inline mb-0">
          <li
            className="list-inline-item"
            onClick={() => onEdit(item.id!)}
          >
            <span className="avtar avtar-s btn-link-success btn-pc-default" role="button">
              <i className="ti ti-edit f-20"></i>
            </span>
          </li>
          <li
            className="list-inline-item"
            onClick={() => onDelete(item)}
          >
            <span className="avtar avtar-s btn-link-danger btn-pc-default" role="button">
              <i className="ti ti-trash f-20"></i>
            </span>
          </li>
        </ul>
      </td>
    </tr>
  );
};

const RoomNameList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<IRoomName[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [nameToDelete, setNameToDelete] = useState<string>("");

  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    changeFilterHandler,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    perPage: PER_PAGE_DEFAULT,
  });
  const navigate = useNavigate();

  const entriesPerPageOptions = [
    { label: "10", value: 10 },
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
    { label: "200", value: 200 },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fetchData = () => {
    RoomNameService.getAll(pagination)
      .then((response) => {
        const data = response.data;
        setList(data);
        setTotalPages(response.totalPages);
      })
      .catch(ErrorToast);
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && list) {
      const oldIndex = list.findIndex((item) => item.id === active.id);
      const newIndex = list.findIndex((item) => item.id === over.id);
      const newList = arrayMove(list, oldIndex, newIndex);
      setList(newList);
      try {
        await RoomNameService.sort(newList.map((item) => item.id!));
        SuccessToast(t("roomName.sortSuccess"));
      } catch (error) {
        ErrorToast(error);
        fetchData();
      }
    }
  };

  const handleDeleteClick = (item: IRoomName) => {
    if (item.id) {
      setItemToDelete(item.id);
      setNameToDelete(item.name || "");
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete !== null) {
      RoomNameService.delete(itemToDelete)
        .then(() => {
          SuccessToast(t("roomName.successDelete"));
          setShowDeleteModal(false);
          setItemToDelete(null);
          fetchData();
        })
        .catch(ErrorToast);
    }
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate("/room-names/create")}
              >
                <i className="ti ti-plus f-18"></i> {t("roomName.add")}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t("job.entriesPerPage")}
                    options={entriesPerPageOptions}
                    onChange={(selected) =>
                      changeFilterHandler(
                        "perPage",
                        selected?.value ?? PER_PAGE_DEFAULT,
                      )
                    }
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === PER_PAGE_DEFAULT,
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t("job.entriesPerPage")}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <form>
                    <input
                      className="form-control"
                      type="text"
                      placeholder={t("roomName.searchPlaceholder")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        changeFilterHandler("term", e.target.value)
                      }
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="table table-hover" id="pc-dt-simple-1">
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}></th>
                      <th>{t("roomItem.id")}</th>
                      <th>{t("roomItem.name")}</th>
                      <th>{t("roomItem.createdAt")}</th>
                      <th>{t("roomItem.updatedAt")}</th>
                      <th className="text-end">{t("roomItem.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length > 0 && (
                      <SortableContext
                        items={list.map((item) => item.id!)}
                        strategy={verticalListSortingStrategy}
                      >
                        {list.map((item: IRoomName) => (
                          <SortableRow
                            key={item.id}
                            item={item}
                            onEdit={(id) => navigate(`/room-names/edit/${id}`)}
                            onDelete={handleDeleteClick}
                            t={t}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </tbody>
                </table>
              </DndContext>
            </div>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              handlePages={handlePages}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={nameToDelete}
      />
    </React.Fragment>
  );
};

export default RoomNameList;
